import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useCurrentFrame } from '../use-current-frame';
import { useVideoConfig } from '../use-video-config';
import { interpolate } from '../interpolate';
import { Easing } from '../Easing';

const ASSET_BASE = 'https://dash.animaker.com/a/u/';

function resolveImageUrl(src) {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  // Paths starting with / are already absolute from web root
  if (src.startsWith('/')) return src;
  // Relative paths (e.g. "assets/frames/...") are served from the public folder
  return `/${src}`;
}

/**
 * Ken Burns patterns — matches the player.html KB array.
 * Each pattern defines start/end values for scale, translateX (%), translateY (%).
 */
const KB_PATTERNS = [
  // kbzi: zoom in + slight pan
  { scaleFrom: 1.0, scaleTo: 1.08, txFrom: 0, txTo: -1, tyFrom: 0, tyTo: -0.5 },
  // kbzo: zoom out from zoomed state
  { scaleFrom: 1.08, scaleTo: 1.0, txFrom: 1, txTo: 0, tyFrom: -0.3, tyTo: 0 },
  // kbpl: pan left at slight zoom
  { scaleFrom: 1.05, scaleTo: 1.05, txFrom: 1.5, txTo: -1.5, tyFrom: 0, tyTo: 0 },
  // kbpr: pan right at slight zoom
  { scaleFrom: 1.05, scaleTo: 1.05, txFrom: -1.5, txTo: 1.5, tyFrom: 0, tyTo: 0 },
];

const CROSSFADE_SECONDS = 0.8;

const FrameContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: transparent;
`;

const ImageLayer = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* cover ensures the image always fills the container — critical for Ken Burns:
     scaleFrom: 1.0 is the "just fills" baseline. With 'contain', images with a
     different intrinsic aspect ratio leave gaps and the white scene background
     shows through at the start of zoom-in before scale grows large enough to cover. */
  object-fit: cover;
  will-change: transform, opacity;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse 100% 100% at 50% 50%,
    transparent 40%,
    rgba(0, 0, 0, 0.3) 75%,
    rgba(0, 0, 0, 0.7) 100%
  );
  z-index: 5;
`;

const LetterboxBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  /* background: #000; — kept transparent to match preview (editor has this commented out too) */
  height: 5%;
  z-index: 6;
`;

/**
 * ImageFrameRenderer — renders scene.images[] as a Ken Burns slideshow
 * with crossfade transitions, all driven by useCurrentFrame() for
 * Puppeteer frame-capture compatibility.
 *
 * Props:
 *   - images: Array of { url, duration, width, height }
 *   - showVignette: boolean (default true)
 *   - showLetterbox: boolean (default true)
 */
export function ImageFrameRenderer({
  images,
  showVignette = true,
  showLetterbox = true,
}) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const crossfadeFrames = Math.round(CROSSFADE_SECONDS * fps);

  // Build cumulative frame ranges for each image within this scene
  const imageTimings = useMemo(() => {
    let cumFrames = 0;
    return images.map((img, i) => {
      const durationFrames = Math.round((img.duration || 2) * fps);
      const startFrame = cumFrames;
      cumFrames += durationFrames;
      return { ...img, url: resolveImageUrl(img.url), startFrame, durationFrames, index: i };
    });
  }, [images, fps]);

  // Find which image is currently active
  let activeIndex = 0;
  for (let i = 0; i < imageTimings.length; i++) {
    if (frame >= imageTimings[i].startFrame) {
      activeIndex = i;
    } else {
      break;
    }
  }

  const activeImage = imageTimings[activeIndex];
  const prevImage = activeIndex > 0 ? imageTimings[activeIndex - 1] : null;

  // Local frame within the active image's time range
  const localFrame = frame - activeImage.startFrame;

  // Crossfade: compute opacity for the active layer (fading in)
  // and the previous layer (fading out)
  const isInCrossfade = localFrame < crossfadeFrames && prevImage !== null;

  let activeOpacity;
  if (isInCrossfade) {
    // Fading in the new image while the previous one fades out
    activeOpacity = interpolate(localFrame, [0, crossfadeFrames], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.inOut(Easing.ease),
    });
  } else {
    activeOpacity = 1;
  }

  const prevOpacity = isInCrossfade
    ? interpolate(localFrame, [0, crossfadeFrames], [1, 0], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: Easing.inOut(Easing.ease),
      })
    : 0;

  // Ken Burns transform for active image
  const activeKB = getKenBurnsStyle(activeImage, localFrame, fps);

  // Ken Burns transform for previous image (frozen at its end state during crossfade)
  let prevKBStyle = {};
  if (prevImage && isInCrossfade) {
    // Show the previous image at its final KB state
    prevKBStyle = getKenBurnsStyle(prevImage, prevImage.durationFrames, fps);
  }

  return (
    <FrameContainer>
      {/* Previous image layer (visible during crossfade) */}
      {prevImage && prevOpacity > 0 && (
        <ImageLayer
          src={prevImage.url}
          style={{
            opacity: prevOpacity,
            transform: prevKBStyle.transform || 'none',
            zIndex: 1,
          }}
          draggable={false}
        />
      )}

      {/* Active image layer */}
      <ImageLayer
        src={activeImage.url}
        style={{
          opacity: activeOpacity,
          transform: activeKB.transform || 'none',
          zIndex: 2,
        }}
        draggable={false}
      />

      {/* Cinematic overlays */}
      {/* {showVignette && <Vignette />} */}
      {showLetterbox && (
        <>
          <LetterboxBar style={{ top: 0 }} />
          <LetterboxBar style={{ bottom: 0 }} />
        </>
      )}
    </FrameContainer>
  );
}

/**
 * Compute Ken Burns transform style for a given image at a local frame position.
 */
function getKenBurnsStyle(imageData, localFrame, fps) {
  const pattern = KB_PATTERNS[imageData.index % KB_PATTERNS.length];
  const durationFrames = imageData.durationFrames;

  const progress = interpolate(
    localFrame,
    [0, durationFrames],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.inOut(Easing.ease) }
  );

  const scale = pattern.scaleFrom + (pattern.scaleTo - pattern.scaleFrom) * progress;
  const tx = pattern.txFrom + (pattern.txTo - pattern.txFrom) * progress;
  const ty = pattern.tyFrom + (pattern.tyTo - pattern.tyFrom) * progress;

  return {
    transform: `scale(${scale}) translate(${tx}%, ${ty}%)`,
  };
}
